// Frontend Encryption Utilities
// Verwendet Web Crypto API für sichere Verschlüsselung

class EmailPasswordEncryption {
  constructor() {
    // Statischer Schlüssel für Frontend-Verschlüsselung
    // In Produktion sollte dieser aus einer sicheren Quelle kommen
    this.keyMaterial = 'BonusClub-Email-Encryption-Key-2024-Secure';
  }

  // Generiere einen Verschlüsselungsschlüssel aus dem Key Material
  async generateKey() {
    const encoder = new TextEncoder();
    const keyMaterial = await window.crypto.subtle.importKey(
      'raw',
      encoder.encode(this.keyMaterial),
      { name: 'PBKDF2' },
      false,
      ['deriveKey']
    );

    return await window.crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: encoder.encode('bonusclub-salt-2024'),
        iterations: 100000,
        hash: 'SHA-256'
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  }

  // Verschlüssele ein Passwort
  async encryptPassword(password) {
    try {
      if (!password || typeof password !== 'string') {
        throw new Error('Invalid password provided');
      }

      const key = await this.generateKey();
      const encoder = new TextEncoder();
      const data = encoder.encode(password);
      
      // Generiere einen zufälligen IV (Initialization Vector)
      const iv = window.crypto.getRandomValues(new Uint8Array(12));
      
      const encrypted = await window.crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: iv },
        key,
        data
      );

      // Kombiniere IV und verschlüsselte Daten
      const encryptedArray = new Uint8Array(encrypted);
      const combined = new Uint8Array(iv.length + encryptedArray.length);
      combined.set(iv);
      combined.set(encryptedArray, iv.length);

      // Konvertiere zu Base64 für Speicherung
      return btoa(String.fromCharCode(...combined));
    } catch (error) {
      console.error('Encryption error:', error);
      throw new Error('Failed to encrypt password');
    }
  }

  // Entschlüssele ein Passwort (nur für Frontend-Validierung)
  async decryptPassword(encryptedPassword) {
    try {
      if (!encryptedPassword || typeof encryptedPassword !== 'string') {
        throw new Error('Invalid encrypted password provided');
      }

      const key = await this.generateKey();
      
      // Konvertiere von Base64
      const combined = new Uint8Array(
        atob(encryptedPassword).split('').map(char => char.charCodeAt(0))
      );
      
      // Extrahiere IV und verschlüsselte Daten
      const iv = combined.slice(0, 12);
      const encryptedData = combined.slice(12);

      const decrypted = await window.crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: iv },
        key,
        encryptedData
      );

      const decoder = new TextDecoder();
      return decoder.decode(decrypted);
    } catch (error) {
      console.error('Decryption error:', error);
      throw new Error('Failed to decrypt password');
    }
  }

  // Prüfe, ob ein Passwort bereits verschlüsselt ist
  isEncrypted(password) {
    if (!password || typeof password !== 'string') return false;
    
    // Verschlüsselte Passwörter sind Base64 und haben eine bestimmte Mindestlänge
    try {
      const decoded = atob(password);
      return decoded.length >= 28; // 12 bytes IV + mindestens 16 bytes encrypted data
    } catch (e) {
      return false;
    }
  }
}

// Singleton-Instanz exportieren
export const emailPasswordEncryption = new EmailPasswordEncryption();

// Convenience-Funktionen
export const encryptEmailPassword = (password) => {
  return emailPasswordEncryption.encryptPassword(password);
};

export const decryptEmailPassword = (encryptedPassword) => {
  return emailPasswordEncryption.decryptPassword(encryptedPassword);
};

export const isPasswordEncrypted = (password) => {
  return emailPasswordEncryption.isEncrypted(password);
}; 